import type { Camera } from '../../services/camerasApi';
import { SHOWCASE2_BOOTH_BINDING } from '../../pages/ShowcaseBackupRoute';
import { isCameraEnabledForEvidence } from '../overview/overviewCameraEvidence';
import type { CameraPatternLinkContext, LinkedCriticalRiskPattern } from '../overview/cameraPatternContext';

function text(value: unknown): string {
  return String(value || '').trim();
}

export function liveViewCanonicalCameraId(cameraId: string): string {
  return text(cameraId).replace(/\.override$/i, '');
}

export function isLiveViewOverrideClone(camera: Pick<Camera, 'camera_id' | 'name'>): boolean {
  return /\.override$/i.test(text(camera.camera_id)) || /\.override$/i.test(text(camera.name));
}

export function hasLiveViewStreamSource(camera: Pick<Camera, 'rtsp_url'>): boolean {
  return Boolean(text(camera.rtsp_url));
}

/** Internal demo-loop titles must not appear as Operator/buyer Live View tiles. */
export function isInternalDemoLiveViewCamera(camera: Pick<Camera, 'name' | 'camera_id'>): boolean {
  const name = text(camera.name);
  if (/demo\s+warehouse\s+camera/i.test(name)) return true;
  if (/\(live\)\s*$/i.test(name) && /demo/i.test(name)) return true;
  return false;
}

/**
 * Bootstrap items expose `cameras.device_id` as `edge_device_id`.
 * The showcase booth device is that binding, not a camera id, title, or RTSP path.
 */
export function isShowcaseBoothLiveViewDevice(camera: Pick<Camera, 'edge_device_id'>): boolean {
  const deviceId = text(camera.edge_device_id);
  return deviceId.length > 0 && deviceId === SHOWCASE2_BOOTH_BINDING.booth_device_id;
}

/**
 * Live View grid = enabled cameras that have an RTSP stream.
 * Drops pipeline-override clones, empty-RTSP rows, internal demo-live titles,
 * and the showcase booth device. Room A and Walkway stay because they pass
 * those checks. A qualification camera with its own RTSP URL stays too.
 * Do not drop `_zone_demo` walkway cameras merely because another device stream exists.
 */
export function selectLiveViewGridCameras(cameras: Camera[]): Camera[] {
  const enabled = (Array.isArray(cameras) ? cameras : []).filter(isCameraEnabledForEvidence);
  return enabled.filter((camera) => (
    !isLiveViewOverrideClone(camera)
    && hasLiveViewStreamSource(camera)
    && !isInternalDemoLiveViewCamera(camera)
    && !isShowcaseBoothLiveViewDevice(camera)
  ));
}

function mergePatternBuckets(
  buckets: Array<CameraPatternLinkContext | null | undefined>,
): CameraPatternLinkContext | null {
  const seen = new Set<string>();
  const patterns: LinkedCriticalRiskPattern[] = [];
  for (const bucket of buckets) {
    for (const pattern of bucket?.patterns || []) {
      const key = text(pattern.linkToPatternReview) || `${pattern.incidentType}|${pattern.groupKey}|${pattern.patternLabel}`;
      if (seen.has(key)) continue;
      seen.add(key);
      patterns.push(pattern);
    }
  }
  if (patterns.length === 0) return null;
  return {
    linkedPatternCount: patterns.length,
    patterns,
    primary: patterns[0] || null,
  };
}

/** Attach .override clone patterns to the canonical camera tile. */
export function lookupLiveViewCameraPatternLink(
  context: Record<string, CameraPatternLinkContext>,
  cameraId: string,
): CameraPatternLinkContext | null {
  const id = text(cameraId);
  if (!id) return null;
  const canonical = liveViewCanonicalCameraId(id);
  return mergePatternBuckets([
    context[id],
    context[canonical],
    context[`${canonical}.override`],
  ]);
}
